import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, isRef as _isRef, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createElementBlock as _createElementBlock, resolveComponent as _resolveComponent, withModifiers as _withModifiers, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "px-4 pt-4 pb-2 flex flex-wrap items-center justify-between gap-4" }
const _hoisted_2 = { class: "text-lg font-medium" }
const _hoisted_3 = { class: "flex flex-wrap items-center gap-2" }
const _hoisted_4 = {
  key: 0,
  class: "py-14 text-center px-4 text-gray-500 text-lg space-y-4"
}
const _hoisted_5 = {
  key: 1,
  class: "py-14 px-4 text-center space-y-2"
}
const _hoisted_6 = { class: "text-lg font-medium text-gray-700" }
const _hoisted_7 = { class: "mx-auto max-w-lg text-gray-500" }
const _hoisted_8 = { key: 2 }
const _hoisted_9 = { class: "text-gray-500" }
const _hoisted_10 = { class: "whitespace-nowrap" }
const _hoisted_11 = {
  key: 0,
  class: "text-gray-400",
  "aria-hidden": "true"
}
const _hoisted_12 = ["title"]
const _hoisted_13 = ["title"]
const _hoisted_14 = {
  key: 1,
  class: "h-1.5 w-16 overflow-hidden rounded bg-gray-200"
}
const _hoisted_15 = {
  key: 0,
  class: "whitespace-nowrap"
}
const _hoisted_16 = {
  key: 1,
  class: "text-gray-400",
  "aria-hidden": "true"
}
const _hoisted_17 = {
  key: 0,
  class: "flex flex-col items-start whitespace-nowrap leading-tight"
}
const _hoisted_18 = { class: "text-sm text-gray-500" }
const _hoisted_19 = {
  key: 1,
  class: "text-gray-500"
}
const _hoisted_20 = {
  key: 1,
  class: "text-gray-400",
  "aria-hidden": "true"
}

import { computed, h } from 'vue';
import { useRouter } from 'vue-router';
import { parseISO } from 'date-fns';
import type { ColumnDef } from '@tanstack/vue-table';
import MainViewCard from '@/components/common/MainViewCard.vue';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ColumnVisibilityMenu, FacetedDataTable } from '@/components/ui/data-table';
import { SearchInput } from '@/components/ui/input';
import {
    lotFraction,
    lotTitle,
    countChipKind,
    countPercent,
    drugUnitLabel,
    formatPercent,
    formatQuantity,
    PERCENT_TONE_CLASSES,
    PERCENT_TONE_TEXT_CLASSES,
    percentTone,
    signedNumber,
    signTone,
} from '@/composables/inventory/inventoryTable';
import { useColumnVisibilityOverrides } from '@/composables/useColumnVisibilityOverrides';
import { i18n } from '@/plugins/internationalization/i18n';
import type { MedicineInventoryRow } from '@/types';
import InventoryActions from './InventoryActions.vue';
import { useInventoryList } from './useInventoryList';

type ToggleColumnId = 'opening' | 'bought' | 'used' | 'wasted' | 'adjustment' | 'last_purchase' | 'last_count';


export default /*@__PURE__*/_defineComponent({
  __name: 'InventoryTable',
  setup(__props) {

const router = useRouter();
const { columnFilters, facetOptions, isError, isFetching, refetch, rows, search, sorting, visibleRows } = useInventoryList();

const heading = (key: string) => i18n.global.t(`views.health.inventory.table.headings.${key}`);

/** A header drawn as two stacked lines, from the translation's own line break; the column menu and the facet title keep the one-line label. */
function brokenHeading(key: string, align: 'left' | 'right' = 'left') {
    return () => {
        const lines = i18n.global.t(`views.health.inventory.table.headingBreaks.${key}`).split('\n');

        return h(
            'span',
            { class: ['flex flex-col leading-tight', align === 'right' ? 'items-end text-right' : 'items-start text-left'] },
            lines.map((line: string) => h('span', line))
        );
    };
}

const TOGGLE_COLUMN_IDS: readonly ToggleColumnId[] = ['opening', 'bought', 'used', 'wasted', 'adjustment', 'last_purchase', 'last_count'];

const TOGGLE_HEADINGS: Record<ToggleColumnId, string> = {
    opening: 'opening',
    bought: 'bought',
    used: 'used',
    wasted: 'wasted',
    adjustment: 'adjustment',
    last_purchase: 'lastPurchase',
    last_count: 'lastCount',
};

/**
 * Spillage and the date of the last purchase are left off by default, so the rest fit beside the
 * sidebar; spillage stays a toggle so the row can still be summed to its balance. The farm, drug and
 * balance always show.
 */
const DEFAULT_HIDDEN: readonly ToggleColumnId[] = ['wasted', 'last_purchase'];

const { isVisible, toggleColumn, setAllColumns, columnToggleOptions } = useColumnVisibilityOverrides<ToggleColumnId>({
    storageKey: 'health.inventory.stock.columnVisibility',
    columnIds: TOGGLE_COLUMN_IDS,
    isAvailable: () => true,
    defaultVisible: (id) => !DEFAULT_HIDDEN.includes(id),
    label: (id) => heading(TOGGLE_HEADINGS[id]),
});

/** Tighter side padding than the table default, to fit as many columns as possible beside the sidebar; the header is a little taller so two-line labels fit. */
const COMPACT = { headerClass: 'px-1.5 h-16', cellClass: 'px-1.5' };

const allColumns = computed<ColumnDef<MedicineInventoryRow>[]>(() => [
    {
        id: 'farm',
        accessorKey: 'farm_name',
        header: heading('farm'),
        enableSorting: false,
        meta: { headerClass: 'h-16' },
    },
    {
        id: 'drug',
        accessorKey: 'drug_name',
        header: heading('drug'),
        enableSorting: false,
        meta: COMPACT,
    },
    {
        id: 'opening',
        accessorKey: 'opening',
        header: brokenHeading('opening', 'right'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
    {
        id: 'bought',
        accessorKey: 'bought',
        header: heading('bought'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
    {
        id: 'used',
        accessorKey: 'used',
        header: heading('used'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
    {
        id: 'wasted',
        accessorKey: 'wasted',
        header: heading('wasted'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
    {
        id: 'adjustment',
        accessorKey: 'adjustment',
        header: heading('adjustment'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
    {
        id: 'closing',
        accessorKey: 'closing',
        header: heading('closing'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
    {
        id: 'last_purchase',
        accessorFn: (row) => row.last_purchase_date ?? '',
        header: brokenHeading('lastPurchase'),
        enableSorting: true,
        meta: COMPACT,
    },
    {
        id: 'last_count',
        accessorFn: (row) => row.last_count?.date ?? '',
        header: brokenHeading('lastCount'),
        enableSorting: false,
        meta: COMPACT,
    },
    {
        id: 'percent',
        accessorFn: (row) => countPercent(row.last_count) ?? -1,
        header: heading('percent'),
        enableSorting: true,
        meta: { align: 'right', ...COMPACT },
    },
]);

const columns = computed(() => allColumns.value.filter((column) => !TOGGLE_COLUMN_IDS.includes(column.id as ToggleColumnId) || isVisible(column.id as ToggleColumnId)));

const facets = computed(() => ({
    farm: { title: heading('farm'), options: facetOptions.value.farm ?? [], selectAll: true },
    drug: { title: heading('drug'), options: facetOptions.value.drug ?? [] },
    last_count: { title: heading('lastCount'), options: facetOptions.value.last_count ?? [] },
}));

function formatDate(date: string): string {
    return i18n.global.d(parseISO(date), 'short');
}

function detailRouteOf(row: MedicineInventoryRow) {
    return { name: 'HealthInventoryDetail', params: { farmId: row.farm_id, drugId: row.drug_id, unitId: row.unit_id } };
}

function openDetail(row: MedicineInventoryRow) {
    router.push(detailRouteOf(row));
}

/** Every row opens its detail page, so every row reads as clickable. */
function rowClassOf(): string {
    return 'cursor-pointer';
}

function rowIdOf(row: MedicineInventoryRow): string {
    return `${row.farm_id}:${row.drug_id}:${row.unit_id}`;
}

return (_ctx: any,_cache: any) => {
  const _component_RouterLink = _resolveComponent("RouterLink")!

  return (_openBlock(), _createBlock(MainViewCard, { class: "overflow-hidden" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("h2", _hoisted_2, _toDisplayString(_ctx.$t('views.health.inventory.table.stockHeading')), 1),
        _createElementVNode("div", _hoisted_3, [
          (!_unref(isError) && _unref(rows).length > 0)
            ? (_openBlock(), _createBlock(_unref(SearchInput), {
                key: 0,
                modelValue: _unref(search),
                "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(search) ? (search).value = $event : null)),
                class: "w-64",
                placeholder: _ctx.$t('views.health.inventory.table.searchPlaceholder')
              }, null, 8, ["modelValue", "placeholder"]))
            : _createCommentVNode("", true),
          _createVNode(InventoryActions),
          (!_unref(isError) && _unref(rows).length > 0)
            ? (_openBlock(), _createBlock(_unref(ColumnVisibilityMenu), {
                key: 1,
                "icon-only": "",
                options: _unref(columnToggleOptions),
                label: _ctx.$t('views.health.inventory.table.columnsButton'),
                onToggle: _unref(toggleColumn),
                onSelectAll: _cache[1] || (_cache[1] = ($event: any) => (_unref(setAllColumns)(true))),
                onDeselectAll: _cache[2] || (_cache[2] = ($event: any) => (_unref(setAllColumns)(false)))
              }, null, 8, ["options", "label", "onToggle"]))
            : _createCommentVNode("", true)
        ])
      ]),
      (_unref(isError))
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createElementVNode("div", null, _toDisplayString(_ctx.$t('views.pigs.records.errors.couldNotLoadData')), 1),
            _createVNode(_unref(Button), {
              variant: "outline",
              onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(refetch)()))
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.$t('views.pigs.records.errors.retry')), 1)
              ]),
              _: 1
            })
          ]))
        : (!_unref(isFetching) && _unref(rows).length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, _toDisplayString(_ctx.$t('views.health.inventory.empty.title')), 1),
              _createElementVNode("div", _hoisted_7, _toDisplayString(_ctx.$t('views.health.inventory.empty.hint')), 1)
            ]))
          : (_openBlock(), _createElementBlock("div", _hoisted_8, [
              _createVNode(_unref(FacetedDataTable), {
                sorting: _unref(sorting),
                "onUpdate:sorting": _cache[5] || (_cache[5] = ($event: any) => (_isRef(sorting) ? (sorting).value = $event : null)),
                "column-filters": _unref(columnFilters),
                "onUpdate:columnFilters": _cache[6] || (_cache[6] = ($event: any) => (_isRef(columnFilters) ? (columnFilters).value = $event : null)),
                data: _unref(visibleRows),
                columns: columns.value,
                facets: facets.value,
                loading: _unref(isFetching),
                "enable-pagination": false,
                page: 1,
                "page-size": Math.max(_unref(visibleRows).length, 1),
                "page-size-options": [],
                "max-body-height": "max-h-[calc(100vh-21rem)]",
                "row-id": rowIdOf,
                "row-class": rowClassOf,
                onRowClick: openDetail
              }, {
                "item[farm]": _withCtx(({ item }) => [
                  _createTextVNode(_toDisplayString(item.farm_name), 1)
                ]),
                "item[drug]": _withCtx(({ item }) => [
                  _createVNode(_component_RouterLink, {
                    to: detailRouteOf(item),
                    class: "font-medium rounded-sm hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-700",
                    onClick: _cache[4] || (_cache[4] = _withModifiers(() => {}, ["stop"]))
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(item.drug_name), 1)
                    ]),
                    _: 2
                  }, 1032, ["to"]),
                  _createElementVNode("span", _hoisted_9, " (" + _toDisplayString(item.unit_name) + ")", 1)
                ]),
                "item[opening]": _withCtx(({ item }) => [
                  _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(formatQuantity)(item.opening)) + " " + _toDisplayString(item.unit_name), 1)
                ]),
                "item[bought]": _withCtx(({ item }) => [
                  _createElementVNode("span", {
                    class: _normalizeClass(["whitespace-nowrap", { 'text-gray-400': item.bought === 0 }])
                  }, _toDisplayString(_unref(formatQuantity)(item.bought)) + " " + _toDisplayString(item.unit_name), 3)
                ]),
                "item[used]": _withCtx(({ item }) => [
                  _createElementVNode("span", {
                    class: _normalizeClass(["whitespace-nowrap", { 'text-gray-400': item.used === 0 }])
                  }, _toDisplayString(_unref(formatQuantity)(item.used)) + " " + _toDisplayString(item.unit_name), 3)
                ]),
                "item[wasted]": _withCtx(({ item }) => [
                  _createElementVNode("span", {
                    class: _normalizeClass(["whitespace-nowrap", { 'text-gray-400': item.wasted === 0 }])
                  }, _toDisplayString(_unref(formatQuantity)(item.wasted)) + " " + _toDisplayString(item.unit_name), 3)
                ]),
                "item[adjustment]": _withCtx(({ item }) => [
                  (item.adjustment === 0)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_11, "–"))
                    : (_openBlock(), _createElementBlock("span", {
                        key: 1,
                        class: _normalizeClass(["whitespace-nowrap", _unref(signTone)(item.adjustment)])
                      }, _toDisplayString(_unref(signedNumber)(item.adjustment, _unref(formatQuantity))) + " " + _toDisplayString(item.unit_name), 3))
                ]),
                "item[closing]": _withCtx(({ item }) => [
                  _createElementVNode("div", {
                    class: "flex flex-col items-end gap-1",
                    title: _unref(lotTitle)(item)
                  }, [
                    _createElementVNode("span", {
                      class: _normalizeClass(["whitespace-nowrap font-semibold", { 'text-red-600': item.closing < 0 }]),
                      title: _unref(drugUnitLabel)(item)
                    }, _toDisplayString(_unref(formatQuantity)(item.closing)) + " " + _toDisplayString(item.unit_name), 11, _hoisted_13),
                    (!item.has_purchase)
                      ? (_openBlock(), _createBlock(_unref(Badge), {
                          key: 0,
                          variant: "outline",
                          class: _normalizeClass(["whitespace-nowrap font-medium", _unref(PERCENT_TONE_CLASSES).error])
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_ctx.$t('views.health.inventory.chips.consumptionWithoutPurchase')), 1)
                          ]),
                          _: 1
                        }, 8, ["class"]))
                      : (_openBlock(), _createElementBlock("div", _hoisted_14, [
                          _createElementVNode("div", {
                            class: "h-full rounded bg-primary-700",
                            style: _normalizeStyle({ width: `${item.active_lot ? _unref(lotFraction)(item.active_lot) * 100 : 0}%` })
                          }, null, 4)
                        ]))
                  ], 8, _hoisted_12)
                ]),
                "item[last_purchase]": _withCtx(({ item }) => [
                  (item.last_purchase_date)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_15, _toDisplayString(formatDate(item.last_purchase_date)), 1))
                    : (_openBlock(), _createElementBlock("span", _hoisted_16, "–"))
                ]),
                "item[last_count]": _withCtx(({ item }) => [
                  (item.last_count)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_17, [
                        _createElementVNode("span", null, _toDisplayString(formatDate(item.last_count.date)), 1),
                        _createElementVNode("span", _hoisted_18, _toDisplayString(_unref(countChipKind)(item.last_count) === 'match' ? _ctx.$t('views.health.inventory.chips.countMatches') : `${_unref(signedNumber)(item.last_count.difference, _unref(formatQuantity))} ${item.unit_name}`), 1)
                      ]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_19, _toDisplayString(_ctx.$t('views.health.inventory.chips.noCount')), 1))
                ]),
                "item[percent]": _withCtx(({ item }) => [
                  (_unref(countPercent)(item.last_count) !== null)
                    ? (_openBlock(), _createElementBlock("span", {
                        key: 0,
                        class: _normalizeClass(["whitespace-nowrap font-medium", _unref(PERCENT_TONE_TEXT_CLASSES)[_unref(percentTone)(_unref(countPercent)(item.last_count)!)]])
                      }, _toDisplayString(_unref(formatPercent)(_unref(countPercent)(item.last_count)!)), 3))
                    : (_openBlock(), _createElementBlock("span", _hoisted_20, "–"))
                ]),
                _: 1
              }, 8, ["sorting", "column-filters", "data", "columns", "facets", "loading", "page-size"])
            ]))
    ]),
    _: 1
  }))
}
}

})